import * as React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Button from '@material-ui/core/Button';
import { withStyles } from '@material-ui/core/styles';
import PublicNav from '../shared/publicNav';
import Logo from '../assets/real-sign.svg';
import UploadIcon from '../assets/upload.svg';
import DocumentIcon from '../assets/document.svg';
import SignatureIcon from '../assets/signature-alt.svg';
import SignatureFullIcon from '../assets/signature.svg';
import InitialIcon from '../assets/initial.svg';
import PersonIcon from '../assets/person.svg';
import SendIcon from '../assets/send.svg';
import CircleCheckIcon from '../assets/circle-check.svg';
import PenIcon from '../assets/pen.svg';
import DateIcon from '../assets/date.svg';
import TextIcon from '../assets/text.svg';
import DownloadIcon from '../assets/download.svg';
import NoteAddIcon from '../assets/note-add.svg';
import MergeIcon from '../assets/merge.svg';
import useInView from '../shared/hooks/useInView';
import useCounter from '../shared/hooks/useCounter';
import styles from './landing.styles';

// ─── Animation helpers ─────────────────────────────────────────────────────────
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.55, delay, ease: [0.25, 0.46, 0.45, 0.94] },
});

const fadeIn = (delay = 0) => ({
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: 0.6, delay },
});

// ─── Shared scroll-reveal wrapper ──────────────────────────────────────────────
function ScrollReveal({ children, delay = 0 }) {
  const [ref, visible] = useInView({ threshold: 0.12 });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={visible ? { opacity: 1, y: 0 } : { opacity: 0, y: 28 }}
      transition={{ duration: 0.55, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      {children}
    </motion.div>
  );
}

// ─── Animated counter stat ─────────────────────────────────────────────────────
function StatItem({ value, suffix, label, classes }) {
  const [ref, visible] = useInView({ threshold: 0.2 });
  const count = useCounter(value, 1800, visible);
  return (
    <div ref={ref} className={classes.statItem}>
      <div className={classes.statValue}>
        {count.toLocaleString()}{suffix}
      </div>
      <div className={classes.statLabel}>{label}</div>
    </div>
  );
}

// ─── Step card ─────────────────────────────────────────────────────────────────
function StepCard({ Icon, number, title, text, index, classes }) {
  const [ref, visible] = useInView({ threshold: 0.1 });
  return (
    <motion.div
      ref={ref}
      className={classes.stepCard}
      initial={{ opacity: 0, y: 40 }}
      animate={visible ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.5, delay: index * 0.12, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      <div className={classes.stepNumberBadge}>{number}</div>
      <div className={classes.stepIconRing}>
        <Icon className={classes.stepIcon} />
      </div>
      <h3 className={classes.stepTitle}>{title}</h3>
      <p className={classes.stepText}>{text}</p>
    </motion.div>
  );
}

// ─── Persona card ──────────────────────────────────────────────────────────────
function PersonaCard({ Icon, role, headline, points, index, classes }) {
  const [ref, visible] = useInView({ threshold: 0.1 });
  return (
    <motion.div
      ref={ref}
      className={classes.personaCard}
      initial={{ opacity: 0, y: 36 }}
      animate={visible ? { opacity: 1, y: 0 } : { opacity: 0, y: 36 }}
      transition={{ duration: 0.5, delay: index * 0.12, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      <div className={classes.personaIconRing}>
        <Icon className={classes.personaIcon} />
      </div>
      <span className={classes.personaRole}>{role}</span>
      <h3 className={classes.personaHeadline}>{headline}</h3>
      <ul className={classes.personaList}>
        {points.map((pt) => (
          <li key={pt} className={classes.personaItem}>
            <span className={classes.personaBullet} />
            {pt}
          </li>
        ))}
      </ul>
    </motion.div>
  );
}

// ─── Benefit card ──────────────────────────────────────────────────────────────
function BenefitCard({ Icon, title, text, tag, index, classes }) {
  const [ref, visible] = useInView({ threshold: 0.1 });
  return (
    <motion.div
      ref={ref}
      className={classes.benefitCard}
      initial={{ opacity: 0, y: 36 }}
      animate={visible ? { opacity: 1, y: 0 } : { opacity: 0, y: 36 }}
      transition={{ duration: 0.5, delay: index * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      <span className={classes.benefitTag}>{tag}</span>
      <div className={classes.benefitIconRing}>
        <Icon className={classes.benefitIcon} />
      </div>
      <h3 className={classes.benefitTitle}>{title}</h3>
      <p className={classes.benefitText}>{text}</p>
    </motion.div>
  );
}

// ─── FAQ item ──────────────────────────────────────────────────────────────────
class FaqItem extends React.Component<{ question: string, answer: string, classes: Object }> {
  state = { open: false };

  toggle = () => this.setState((s) => ({ open: !s.open }));

  render() {
    const { question, answer, classes } = this.props;
    const { open } = this.state;
    return (
      <div className={`${classes.faqItem} ${open ? classes.faqItemOpen : ''}`}>
        <button type="button" className={classes.faqQuestion} onClick={this.toggle}>
          <span>{question}</span>
          <span className={`${classes.faqChevron} ${open ? classes.faqChevronOpen : ''}`}>
            &#8964;
          </span>
        </button>
        {open && (
          <motion.div
            className={classes.faqAnswer}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            transition={{ duration: 0.28, ease: 'easeOut' }}
          >
            <p>{answer}</p>
          </motion.div>
        )}
      </div>
    );
  }
}

// ─── Data ──────────────────────────────────────────────────────────────────────
const steps = [
  {
    Icon: UploadIcon,
    number: '01',
    title: 'Upload your PDF',
    text: 'Import any PDF document—contracts, agreements, forms—directly into the platform. No conversion needed.',
  },
  {
    Icon: NoteAddIcon,
    number: '02',
    title: 'Assign signers',
    text: 'Designate where each party needs to sign, initial, or provide a date. Add multiple signers in any order.',
  },
  {
    Icon: SendIcon,
    number: '03',
    title: 'Send and track',
    text: 'Recipients receive a secure signing link by email and can sign from any browser—no account required.',
  },
  {
    Icon: DownloadIcon,
    number: '04',
    title: 'Completed and certified',
    text: 'Once all parties have signed, a tamper-evident, audit-stamped PDF is archived and available for download.',
  },
];

const PRIMARY = '#0f62bd';

const personas = [
  {
    Icon: PersonIcon,
    role: 'Real Estate Agent',
    headline: 'Close transactions from anywhere',
    points: [
      'Send listing agreements and purchase contracts in seconds',
      'Track exactly who has opened, reviewed, and signed',
      'Multi-signer order ensures docs flow to the right parties',
      'Clients sign on any device—no account or app required',
    ],
  },
  {
    Icon: SignatureIcon,
    role: 'Brokerage Administrator',
    headline: 'Govern your entire brokerage',
    points: [
      'Manage agents, admins and permissions from one dashboard',
      'Access a full audit trail of every signed document',
      'Role-based access keeps sensitive data protected',
      'Real-time visibility across all open transactions',
    ],
  },
  {
    Icon: CircleCheckIcon,
    role: 'Client / Recipient',
    headline: 'Sign in seconds—no signup needed',
    points: [
      'Receive a secure link directly to your email inbox',
      'Review and sign from any browser or mobile device',
      'Guided signing experience with clear field indicators',
      'Download a certified copy of completed documents',
    ],
  },
];

const benefits = [
  {
    Icon: SignatureIcon,
    title: 'Legally binding signatures',
    text: 'Electronic signatures collected through RealDesk eSign meet ESIGN and UETA standards. Each signature is tied to a verifiable identity and a precise timestamp.',
    tag: 'Legal',
  },
  {
    Icon: DocumentIcon,
    title: 'Request signatures from anyone',
    text: 'Send signing requests to clients, co-workers, or third parties. Recipients do not need an account—they receive a direct, secure link and sign in seconds.',
    tag: 'Sharing',
  },
  {
    Icon: CircleCheckIcon,
    title: 'Complete audit trail',
    text: 'Every action—document viewed, field signed, session completed—is recorded with timestamp, IP address, and device information.',
    tag: 'Compliance',
  },
  {
    Icon: PersonIcon,
    title: 'Role-based access control',
    text: 'Agents, administrators, and brokerage managers each see only what they need. Super-admin controls let you govern access across your entire organization.',
    tag: 'Security',
  },
];

const securityPoints = [
  {
    title: '256-bit TLS encryption',
    text: 'All data in transit is protected by TLS 1.2+ with 256-bit AES encryption—the same standard used by financial institutions worldwide.',
    color: '#3b82f6',
  },
  {
    title: 'Tamper-evident sealing',
    text: 'Completed documents are cryptographically sealed. Any alteration after signing is immediately detectable and invalidates the document.',
    color: '#8b5cf6',
  },
  {
    title: 'Multi-factor identity verification',
    text: 'Each signer is verified through a unique link, email confirmation, and session token tied to their device and IP address at the time of signing.',
    color: '#10b981',
  },
  {
    title: 'ESIGN & UETA compliant',
    text: 'RealDesk eSign signatures satisfy the requirements of the U.S. Electronic Signatures in Global and National Commerce Act and the Uniform Electronic Transactions Act.',
    color: '#f59e0b',
  },
  {
    title: 'Permanent audit log',
    text: 'A time-stamped log of every event—from document creation to the final download—is stored with the completed file and available for export.',
    color: '#ef4444',
  },
  {
    title: 'Secure document storage',
    text: 'Completed PDFs are stored with server-side encryption and are only accessible to authorized parties. Documents are never shared or used for third-party purposes.',
    color: '#06b6d4',
  },
];

const faqs = [
  {
    question: 'Are electronic signatures legally valid?',
    answer: 'Yes. Electronic signatures are legally binding in the United States under the ESIGN Act (2000) and the Uniform Electronic Transactions Act (UETA), as well as under eIDAS in the European Union and similar legislation in most countries. Signatures collected through RealDesk eSign carry the same legal weight as handwritten signatures.',
  },
  {
    question: 'Do recipients need to create an account to sign?',
    answer: 'No. Signers receive a secure, unique link by email. They can open the document, review it, and sign directly in their browser without creating an account or downloading any software.',
  },
  {
    question: 'What types of documents can I upload?',
    answer: 'RealDesk eSign supports any standard PDF document. You can upload contracts, purchase agreements, listing forms, disclosure documents, lease agreements, and any other PDF you need signed.',
  },
  {
    question: 'How many people can sign a single document?',
    answer: 'There is no hard limit on the number of signers per document. You can add multiple signers and define the order in which each must complete their fields before the document advances to the next party.',
  },
  {
    question: 'Can I verify that a completed document has not been altered?',
    answer: 'Yes. Every completed document is cryptographically sealed. You can use the Verify Signature tool on this platform to independently confirm the authenticity and integrity of any document signed through RealDesk eSign.',
  },
  {
    question: 'How long are completed documents stored?',
    answer: 'Completed, signed documents are stored securely on our servers and remain accessible to authorized parties. You can download a certified copy at any time from within the platform.',
  },
  {
    question: 'Is my document data private?',
    answer: 'Absolutely. Your documents are stored with server-side encryption and are accessible only to the parties you designate. RealDesk does not access, share, or use your document content for any purpose outside of providing the service.',
  },
];

// ─── Page ──────────────────────────────────────────────────────────────────────
function LandingPageC({ classes }) {
  return (
    <div className={classes.root}>

      {/* ─── Nav ─────────────────────────────────────────────── */}
      <PublicNav
        centerLinks={[
          { label: 'How it works', href: '#how-it-works' },
          { label: 'Features', href: '#features' },
          { label: 'Verify a signature', to: '/s' },
        ]}
        actions={
          <React.Fragment>
            <Button component={Link} to="/login" variant="outlined" className={classes.navSignIn}>
              Log in
            </Button>
            <Button component={Link} to="/signup/agent/0" variant="contained" className={classes.navGetStarted}>
              Get started
            </Button>
          </React.Fragment>
        }
      />

      {/* ─── Hero ────────────────────────────────────────────── */}
      <section className={classes.hero}>
        <div className={classes.heroOverlay} />
        <div className={classes.heroContent}>
          <motion.p className={classes.heroEyebrow} {...fadeUp(0.1)}>
            PDF document signing
          </motion.p>
          <motion.h1 className={classes.heroHeadline} {...fadeUp(0.2)}>
            Sign documents.<br />
            Request signatures.<br />
            Close deals faster.
          </motion.h1>
          <motion.p className={classes.heroSubhead} {...fadeUp(0.35)}>
            RealDesk eSign lets you prepare, send, sign, and manage PDF documents from a single platform—designed for real estate professionals and their clients.
          </motion.p>
          <motion.div className={classes.heroCtas} {...fadeUp(0.48)}>
            <Button component={Link} to="/login" variant="contained" size="large" className={classes.heroPrimary}>
              Start signing for free
            </Button>
            <Button component="a" href="#how-it-works" variant="outlined" size="large" className={classes.heroSecondary}>
              See how it works
            </Button>
          </motion.div>
        </div>
        <motion.div className={classes.scrollHint} {...fadeIn(1.2)}>
          <div className={classes.scrollDot} />
        </motion.div>
      </section>

      {/* ─── Trust bar ───────────────────────────────────────── */}
      <motion.div className={classes.trustBar} {...fadeIn(0.55)}>
        <span className={classes.trustItem}>Legally binding</span>
        <span className={classes.trustDivider} />
        <span className={classes.trustItem}>ESIGN &amp; UETA compliant</span>
        <span className={classes.trustDivider} />
        <span className={classes.trustItem}>Tamper-evident audit trail</span>
        <span className={classes.trustDivider} />
        <span className={classes.trustItem}>No app download required</span>
      </motion.div>

      {/* ─── Stats ───────────────────────────────────────────── */}
      <section className={classes.stats}>
        <div className={classes.sectionInner}>
          <div className={classes.statsGrid}>
            <StatItem value={50000} suffix="+" label="Documents signed" classes={classes} />
            <StatItem value={99} suffix="%" label="Customer satisfaction" classes={classes} />
            <StatItem value={3} suffix=" min" label="Average time to sign" classes={classes} />
            <StatItem value={100} suffix="%" label="Browser-based — no install" classes={classes} />
          </div>
        </div>
      </section>

      {/* ─── What is eSign ───────────────────────────────────── */}
      <section className={classes.intro}>
        <div className={classes.sectionInner}>
          <ScrollReveal>
            <p className={classes.sectionEyebrow}>What is eSign?</p>
            <h2 className={classes.sectionHeadline}>
              A complete electronic signature solution for PDF documents
            </h2>
            <p className={classes.introParagraph}>
              An electronic signature platform replaces pen-and-paper signing with a secure, digital process. Instead of printing, scanning, and emailing documents back and forth, signers receive a link, open the document in their browser, and sign in seconds.
            </p>
            <p className={classes.introParagraph}>
              RealDesk eSign goes further—letting you define exactly who signs where, set the signing order, track progress in real time, and archive a certified, tamper-evident copy once every party has completed the document.
            </p>
            <p className={classes.introParagraph}>
              Electronic signatures collected through RealDesk eSign carry the same legal weight as wet ink signatures under ESIGN, UETA, and most international equivalents. Each signature is bound to a unique identity token, timestamp, and IP address.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* ─── Product demo ────────────────────────────────────── */}
      <section id="demo" className={classes.demo}>
        <div className={classes.sectionInner}>
          <ScrollReveal>
            <p className={classes.sectionEyebrowOnDark}>See it in action</p>
            <h2 className={classes.sectionHeadlineOnDark}>The complete signing workflow, end to end</h2>
            <p className={classes.demoSubhead}>
              From placing signature fields on a contract to a client signing on their phone—the entire process takes minutes, not days.
            </p>
          </ScrollReveal>

          <div className={classes.demoGrid}>
            {/* panel 1 */}
            <ScrollReveal delay={0.05}>
              <div className={classes.demoPanel}>
                <div className={classes.demoBrowserBar}>
                  <span className={classes.demoDot} style={{ backgroundColor: '#ef4444' }} />
                  <span className={classes.demoDot} style={{ backgroundColor: '#f59e0b' }} />
                  <span className={classes.demoDot} style={{ backgroundColor: '#10b981' }} />
                  <span className={classes.demoUrl}>app.realdesk.com / prepare</span>
                </div>
                <div className={classes.demoScreen}>
                  <img src="/assets/form100/1.gif" alt="Preparing a document for signing" className={classes.demoGif} />
                </div>
                <div className={classes.demoPanelCaption}>
                  <PenIcon className={classes.demoCaptionIcon} />
                  <div>
                    <strong className={classes.demoCaptionTitle}>Prepare &amp; place fields</strong>
                    <p className={classes.demoCaptionText}>Drag and drop signature, initial, date, and text fields exactly where each signer needs to act.</p>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            {/* panel 2 */}
            <ScrollReveal delay={0.15}>
              <div className={classes.demoPanel}>
                <div className={classes.demoBrowserBar}>
                  <span className={classes.demoDot} style={{ backgroundColor: '#ef4444' }} />
                  <span className={classes.demoDot} style={{ backgroundColor: '#f59e0b' }} />
                  <span className={classes.demoDot} style={{ backgroundColor: '#10b981' }} />
                  <span className={classes.demoUrl}>app.realdesk.com / sign</span>
                </div>
                <div className={classes.demoScreen}>
                  <img src="/assets/form100/2.gif" alt="Signing a document as a recipient" className={classes.demoGif} />
                </div>
                <div className={classes.demoPanelCaption}>
                  <CircleCheckIcon className={classes.demoCaptionIcon} />
                  <div>
                    <strong className={classes.demoCaptionTitle}>Recipients sign in seconds</strong>
                    <p className={classes.demoCaptionText}>Guided field-by-field experience on any device. No account, no download, no friction.</p>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ─── Who it's for ────────────────────────────────────── */}
      <section id="who" className={classes.personas}>
        <div className={classes.sectionInner}>
          <ScrollReveal>
            <p className={classes.sectionEyebrowOnDark}>Who it&apos;s for</p>
            <h2 className={classes.sectionHeadlineOnDark}>Built for every role in the transaction</h2>
          </ScrollReveal>
          <div className={classes.personasGrid}>
            {personas.map(({ Icon, role, headline, points }, i) => (
              <PersonaCard
                key={role}
                Icon={Icon}
                role={role}
                headline={headline}
                points={points}
                index={i}
                classes={classes}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ─── How it works ────────────────────────────────────── */}
      <section id="how-it-works" className={classes.steps}>
        <div className={classes.sectionInner}>
          <ScrollReveal>
            <p className={classes.sectionEyebrow}>How it works</p>
            <h2 className={classes.sectionHeadline}>From upload to signed in four steps</h2>
          </ScrollReveal>
          <div className={classes.stepsConnectorRow}>
            {steps.map((step, i) => (
              <div key={step.number} className={classes.stepsConnectorCell}>
                <div className={classes.stepsConnectorDot} />
                {i < steps.length - 1 && <div className={classes.stepsConnectorLine} />}
              </div>
            ))}
          </div>
          <div className={classes.stepsGrid}>
            {steps.map(({ Icon, number, title, text }, i) => (
              <StepCard key={number} Icon={Icon} number={number} title={title} text={text} index={i} classes={classes} />
            ))}
          </div>
        </div>
      </section>

      {/* ─── Field types ─────────────────────────────────────── */}
      <section className={classes.fieldTypes}>
        <div className={classes.sectionInner}>
          <ScrollReveal>
            <p className={classes.sectionEyebrowOnDark}>Field types</p>
            <h2 className={classes.sectionHeadlineOnDark}>Every field your document needs</h2>
            <p className={classes.fieldTypesSubhead}>
              Place any combination of interactive fields on your PDF. Each field is assigned to a specific signer and is required before the document can advance.
            </p>
          </ScrollReveal>
          <div className={classes.fieldTypesGrid}>
            {[
              { Icon: SignatureFullIcon, label: 'Signature', desc: 'Drawn, typed, or uploaded — legally binding signature captured in place.' },
              { Icon: InitialIcon,       label: 'Initials',  desc: 'Quick initial fields for page-by-page acknowledgement or confirmation.' },
              { Icon: DateIcon,          label: 'Date',      desc: 'Auto-populated with the date the signer completes their fields.' },
              { Icon: TextIcon,          label: 'Text',      desc: 'Free-form text entry for names, addresses, reference numbers, and more.' },
              { Icon: MergeIcon,         label: 'Merge PDF', desc: 'Combine multiple PDF pages into a single document before sending.' },
              { Icon: DownloadIcon,      label: 'Download',  desc: 'Certified, tamper-evident copy available for download once all parties have signed.' },
            ].map(({ Icon, label, desc }, i) => (
              <ScrollReveal key={label} delay={i * 0.06}>
                <div className={classes.fieldTypeCard}>
                  <div className={classes.fieldTypeIconRing}>
                    <Icon className={classes.fieldTypeIcon} />
                  </div>
                  <h3 className={classes.fieldTypeLabel}>{label}</h3>
                  <p className={classes.fieldTypeDesc}>{desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Features ────────────────────────────────────────── */}
      <section id="features" className={classes.benefits}>
        <div className={classes.sectionInner}>
          <ScrollReveal>
            <p className={classes.sectionEyebrowLight}>Features</p>
            <h2 className={classes.sectionHeadlineLight}>Built for the way professionals work</h2>
          </ScrollReveal>
          <div className={classes.benefitsGrid}>
            {benefits.map(({ Icon, title, text, tag }, i) => (
              <BenefitCard key={title} Icon={Icon} title={title} text={text} tag={tag} index={i} classes={classes} />
            ))}
          </div>
        </div>
      </section>

      {/* ─── Security ────────────────────────────────────────── */}
      <section id="security" className={classes.security}>
        <div className={classes.sectionInner}>
          <ScrollReveal>
            <p className={classes.sectionEyebrowOnDark}>Security &amp; Compliance</p>
            <h2 className={classes.sectionHeadlineOnDark}>Enterprise-grade protection for every document</h2>
            <p className={classes.securitySubhead}>
              From the moment you upload a document to the day you archive it, every byte is protected by industry-leading security standards and backed by a complete, immutable audit record.
            </p>
          </ScrollReveal>
          <div className={classes.securityGrid}>
            {securityPoints.map(({ title, text, color }, i) => (
              <ScrollReveal key={title} delay={i * 0.07}>
                <div className={classes.securityCard}>
                  <div className={classes.securityDot} style={{ backgroundColor: color }} />
                  <h3 className={classes.securityTitle}>{title}</h3>
                  <p className={classes.securityText}>{text}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FAQ ─────────────────────────────────────────────── */}
      <section id="faq" className={classes.faq}>
        <div className={classes.faqInner}>
          <ScrollReveal>
            <p className={classes.sectionEyebrow}>FAQ</p>
            <h2 className={classes.sectionHeadline}>Common questions</h2>
          </ScrollReveal>
          <div className={classes.faqList}>
            {faqs.map(({ question, answer }) => (
              <FaqItem key={question} question={question} answer={answer} classes={classes} />
            ))}
          </div>
        </div>
      </section>

      {/* ─── Verify CTA ──────────────────────────────────────── */}
      <section id="verify" className={classes.verify}>
        <div className={classes.verifyInner}>
          <ScrollReveal>
            <h2 className={classes.verifyHeadline}>Need to verify a signature?</h2>
            <p className={classes.verifyText}>
              Anyone can independently verify the authenticity of a document signed through RealDesk eSign. No account required.
            </p>
            <Button component={Link} to="/s" variant="outlined" size="large" className={classes.verifyBtn}>
              Verify a document
            </Button>
          </ScrollReveal>
        </div>
      </section>

      {/* ─── Final CTA ───────────────────────────────────────── */}
      <section className={classes.finalCta}>
        <div className={classes.sectionInner}>
          <ScrollReveal>
            <h2 className={classes.finalHeadline}>Ready to go paperless?</h2>
            <p className={classes.finalSub}>Sign your first document today—no credit card required.</p>
            <Button component={Link} to="/login" variant="contained" size="large" className={classes.finalBtn}>
              Get started free
            </Button>
          </ScrollReveal>
        </div>
      </section>

      {/* ─── Footer ──────────────────────────────────────────── */}
      <footer className={classes.footer}>
        <div className={classes.footerInner}>
          <div className={classes.footerLogo}><Logo /></div>
          <p className={classes.footerCopy}>
            &copy; {new Date().getFullYear()} RealDesk. All rights reserved.
          </p>
          <div className={classes.footerLinks}>
            <Link to="/login" className={classes.footerLink}>Log in</Link>
            <Link to="/s" className={classes.footerLink}>Verify signature</Link>
          </div>
        </div>
      </footer>

    </div>
  );
}

export default withStyles(styles, { withTheme: true })(LandingPageC);
